import { FormattedMessage } from 'react-intl';
import { DATE_FORMAT } from '@ui-framework/index';
import dayjs from 'dayjs';
import { Box, IconButton, Tooltip } from '@mui/material';
import ReadMoreText from '@ui-framework/components/UI/ReadMoreText';
import MultiValueDisplay from '@ui-framework/helpers/MultiValueDisplay';
import { Link } from 'react-router-dom';
import DispatchEventStatus from '../../../../../DispatchEventStatus';

function id(original) {
  return original?.id || '';
}

function dispatchId(original) {
  return original?.dispatchId || '';
}

function serviceProviderId(original) {
  return original?.serviceProviderId || '';
}

function serviceProviderName(original) {
  return (
    <Link
      target="_blank"
      rel="noopener noreferrer"
      to={`/serviceproviders/edit/${original?.serviceProvider?.id}`}>
      {original?.serviceProvider?.name || ''}
    </Link>
  );
}

function locationName(original, row, additional) {
  if (!original?.dispatchEventLocation?.locationName) {
    return '';
  }
  const { IconLibrary } = additional;
  return (
    <Box sx={{ justifyContent: 'space-between', display: 'flex' }}>
      <Link
        target="_blank"
        rel="noopener noreferrer"
        to={`/serviceproviders/edit/${original?.serviceProvider?.id}/locations/${original?.dispatchEventLocation?.id}`}>
        {original?.dispatchEventLocation?.locationName || ''}
      </Link>
      <Tooltip
        title={
          <FormattedMessage
            id="VIEW_DISPATCH_LOCATION_ON_MAP"
            defaultMessage="View Dispatch Location On Map"
          />
        }
        arrow
        placement="left">
        <IconButton
          target="_blank"
          rel="noopener noreferrer"
          component="a"
          href={`/servicejobs/map/${additional?.serviceJobId}/${original?.dispatchEventLocation?.id}`}
          sx={{
            width: '25px',
            height: '25px',
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
          variant="contained">
          <IconLibrary.Location sx={{ fontSize: '15px' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function acceptedLocationName(original, row, additional) {
  if (!original?.acceptedLocation?.locationName) {
    return '';
  }
  const { IconLibrary } = additional;
  return (
    <Box sx={{ justifyContent: 'space-between', display: 'flex' }}>
      <Link
        target="_blank"
        rel="noopener noreferrer"
        to={`/serviceproviders/edit/${original?.serviceProvider?.id}/locations/${original?.acceptedLocation?.id}`}>
        {original?.acceptedLocation?.locationName || '' || ''}
      </Link>
      <Tooltip
        title={
          <FormattedMessage
            id="VIEW_ACCEPTED_LOCATION_ON_MAP"
            defaultMessage="View Accepted Location On Map"
          />
        }
        arrow
        placement="right">
        <IconButton
          target="_blank"
          rel="noopener noreferrer"
          component="a"
          href={`/servicejobs/map/${additional?.serviceJobId}/${original?.acceptedLocation?.id}`}
          sx={{
            width: '25px',
            height: '25px',
            backgroundColor: 'success.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'success.dark',
            },
          }}
          variant="contained">
          <IconLibrary.Location sx={{ fontSize: '15px' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function status(original) {
  return <DispatchEventStatus showAsText value={original?.status} />;
}

function serviceDate(original) {
  return original?.serviceDate
    ? dayjs(original?.serviceDate).format(DATE_FORMAT.MEDIUM_DATE)
    : '';
}

function acceptanceDate(original) {
  return original?.acceptanceDate
    ? dayjs(original?.acceptanceDate).format(DATE_FORMAT.LONG_DATE)
    : '';
}

function rejectionDate(original) {
  const data = [];
  if (original.status === 'REJECTED') {
    if (original?.rejectionDate) {
      data.push(
        <MultiValueDisplay
          appendColon
          title={
            <FormattedMessage id="REJECT_DATE" defaultMessage="Reject Date" />
          }>
          {dayjs(original?.rejectionDate).format(DATE_FORMAT.LONG_DATE)}
        </MultiValueDisplay>,
      );
    }
    if (original?.reason) {
      data.push(
        <MultiValueDisplay
          appendColon
          title={
            <FormattedMessage
              id="REJECT_REASON"
              defaultMessage="Reject Reason"
            />
          }
          sx={{ mb: 1 }}>
          <ReadMoreText>{original?.reason}</ReadMoreText>
        </MultiValueDisplay>,
      );
    }
  }
  return data.map(d => <Box>{d}</Box>);
}

function lastModifiedDateTime(original) {
  return original?.lastModifiedDateTime
    ? dayjs(original?.lastModifiedDateTime).format(DATE_FORMAT.LONG_DATE)
    : '';
}

function createdDateTime(original) {
  return original?.createdDateTime
    ? dayjs(original?.createdDateTime).format(DATE_FORMAT.LONG_DATE)
    : '';
}

function conditions(original) {
  const data = [];
  if (original.status === 'ACCEPTED_WITH_CONDITIONS') {
    if (original?.serviceDate) {
      data.push(
        <MultiValueDisplay
          appendColon
          title={
            <FormattedMessage id="SERVICE_DATE" defaultMessage="Service Date" />
          }>
          {dayjs(original?.serviceDate).format(DATE_FORMAT.MEDIUM_DATE)}
        </MultiValueDisplay>,
      );
    }
    if (original?.additionalMoney > 0) {
      data.push(
        <MultiValueDisplay
          appendColon
          type="currency"
          title={
            <FormattedMessage
              id="ADDITIONAL_MONEY"
              defaultMessage="Additional Money"
            />
          }
          sx={{ mb: 1 }}>
          {original?.additionalMoney}
        </MultiValueDisplay>,
      );
    }
    if (original?.reason) {
      data.push(
        <MultiValueDisplay
          appendColon
          title={
            <FormattedMessage id="OTHER_REASON" defaultMessage="Other reason" />
          }
          sx={{ mb: 1 }}>
          <ReadMoreText>{original?.reason}</ReadMoreText>
        </MultiValueDisplay>,
      );
    }
    return data?.map(d => <Box>{d}</Box>);
  }
  return '';
}

export {
  id,
  dispatchId,
  serviceProviderId,
  serviceProviderName,
  locationName,
  acceptedLocationName,
  status,
  serviceDate,
  acceptanceDate,
  rejectionDate,
  lastModifiedDateTime,
  createdDateTime,
  conditions,
};
